import {
  Box,
  Dialog, DialogContent, DialogTitle,
  Typography,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { PaymentMethodStatus, PayoutDialogContentType } from '../../../common/enums';
import PaypalVerificationCard from './PaypalVerificationCard';
import { BankAccountForm } from './BankAccountForm';
import { PaperCheckDetails } from './PaperCheckDetails';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { selectBankAccount, selectPaperCheque, selectPayPalAccount } from '../../../store/slices/dashboardSlice';
import { PaymentMethodType } from '../../../common/models/PaymentMethod';
import { activatePaymentMethod, getPaymentMethods, unlinkPaymentMethod } from '../../../store/thunks/dashboardThunk';
import { TestIds } from '../../../assets/testIds';
import { PaymentMethodCard } from './PaymentMethodCard';
import { RemovePaymentMethodConfirm } from './RemovePaymentMethodConfirm';
import { ActivePaymentMethodConfirm } from './ActivePaymentMethodConfirm';
import { useCustomEventTracking } from '../../../common/useCustomEventTracking';
import { PaymentMethodDetails } from './Common';
import { Logger } from '../../../config/logging/logger';
import { useSolutionOrg } from '../../../config/useSolutionOrg';

type ManagePayoutSettingsDialogProps = {
  onBtnClick?: () => void;
  setOpenDialog?: (event: boolean) => void;
  open: boolean;
};

export function ManagePayoutSettingsDialog({
  open,
  setOpenDialog,
}: ManagePayoutSettingsDialogProps) {
  const theme = useTheme();
  const { t } = useTranslation();
  const [paymentMethodId, setPaymentMethodId] = useState('');
  const [paymentMethodType, setPaymentMethodType] = useState('');
  const [dialogContentType, setDialogContentType] =
    useState(PayoutDialogContentType.PAYMENT_METHOD_LIST);
  const [dialogSubTitle, setDialogSubTitle] =
    useState<string | JSX.Element>(`${t('wallet.payoutSettingsDialog.subTitle.connectNewAccount')}`);
  const payPalAccount = useAppSelector(selectPayPalAccount);
  const bankAccount = useAppSelector(selectBankAccount);
  const paperCheque = useAppSelector(selectPaperCheque);
  const dispatch = useAppDispatch();
  const { trackPayoutSetting } = useCustomEventTracking();
  const paymentMethods = PaymentMethodDetails();
  const logger = Logger.create('ManagePayoutSettingsDialog');

  const { isNativeAppExperience } = useSolutionOrg();

  useEffect(() => {
    if (payPalAccount || bankAccount || paperCheque) {
      setDialogSubTitle(`${t('wallet.payoutSettingsDialog.subTitle.manageAccount')}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payPalAccount, bankAccount, paperCheque]);

  const handleClose = () => {
    if (dialogContentType === PayoutDialogContentType.BANK_TRANSFER_FORM) {
      dispatch(getPaymentMethods());
    }
    setOpenDialog?.(false);
  };

  const confirmActivePaymentMethod = async (activePaymentId: string) => {
    if (activePaymentId) {
      const paymentMethod =
        Object.values(paymentMethods)?.find((method) => method.id === activePaymentId);
      await dispatch(activatePaymentMethod({
        paymentMethodId: activePaymentId,
      })).then(() => {
        let trackingName = 'BankAccountDefault';
        if (paymentMethod?.type === PaymentMethodType.PAYPAL) {
          trackingName = 'PayPalDefault';
        } else if (paymentMethod?.type === PaymentMethodType.PAPER_CHEQUE) {
          trackingName = 'PaperCheckDefault';
        }
        trackPayoutSetting('Click', trackingName);
      })
        .catch((error: unknown) => logger.error(Logger.format(error)));
      dispatch(getPaymentMethods());
    }
    setOpenDialog?.(false);
  };

  // unlink paypal, bank account, and paper check
  const confirmDeletePaymentMethod = async (deletePaymentId: string) => {
    if (deletePaymentId) {
      await dispatch(unlinkPaymentMethod({ paymentMethodId: deletePaymentId }));
      let unlinkName = 'BankAccount';
      if (paymentMethodType === PaymentMethodType.PAYPAL) {
        unlinkName = 'Paypal';
      } else if (paymentMethodType === PaymentMethodType.PAPER_CHEQUE) {
        unlinkName = 'PaperCheck';
      }
      trackPayoutSetting('Unlink', unlinkName);
      dispatch(getPaymentMethods());
    }
    setOpenDialog?.(false);
  };

  // Members with paper check history see all three options
  // (excludes only UNVERIFIED - users who never received a paper check)
  // UNLINKED paper checks are shown so users can re-select them (SN-1063)
  const hasPaperCheckHistory = paperCheque &&
    paperCheque.status !== PaymentMethodStatus.UNVERIFIED;

  // list of payment methods
  const PaymentMethodList = () => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(3),
        alignItems: 'stretch',
      }}
    >
      {/*
        ACH and PayPal are offered to every tier on both platforms, per the Payout
        Methods chart on "SN Logic on the Edge platform": an Association-or-higher
        member whose company has had a rebate paid by paper check gets paper check
        *in addition to* the electronic methods, not instead of them (SN-996).
      */}
      {!isNativeAppExperience && <PaymentMethodCard
        type={PaymentMethodType.PAYPAL}
        onManageClick={() => {
          trackPayoutSetting('Click', 'ManagePayPal');
          setPaymentMethodType(PaymentMethodType.PAYPAL);
          setDialogContentType(PayoutDialogContentType.PAYPAL_CONNECT);
        }}
        setDialogContentType={setDialogContentType}
        setDialogSubTitle={setDialogSubTitle}
        setPaymentMethodId={setPaymentMethodId}
      />}
      <PaymentMethodCard
        type={PaymentMethodType.BANK_ACCOUNT}
        onManageClick={() => {
          trackPayoutSetting('Click', 'ManageBankAccount');
          setPaymentMethodType(PaymentMethodType.BANK_ACCOUNT);
          setDialogContentType(PayoutDialogContentType.BANK_TRANSFER_FORM);
        }}
        setDialogContentType={setDialogContentType}
        setDialogSubTitle={setDialogSubTitle}
        setPaymentMethodId={setPaymentMethodId}
      />
      {/*
        Paper check only appears once the company has actually been paid a rebate by
        check, which is what creates the payment method record. It is never
        self-serve, hence `disableConnect` for a paid SN member.
      */}
      {hasPaperCheckHistory && <PaymentMethodCard
        type={PaymentMethodType.PAPER_CHEQUE}
        onManageClick={() => {
          trackPayoutSetting('Click', 'ManagePaperCheck');
          setPaymentMethodType(PaymentMethodType.PAPER_CHEQUE);
          setDialogContentType(PayoutDialogContentType.PAPER_CHECK_DETAILS);
          // Show "Connect" subtitle for UNLINKED, "Manage" for others (SN-1063)
          const subtitle = paperCheque?.status === PaymentMethodStatus.UNLINKED ?
            t('wallet.payoutSettingsDialog.subTitle.connectNewPaperCheck') :
            t('wallet.payoutSettingsDialog.subTitle.managePaperCheckAccount');
          setDialogSubTitle(subtitle);
        }}
        setDialogContentType={setDialogContentType}
        setDialogSubTitle={setDialogSubTitle}
        setPaymentMethodId={setPaymentMethodId}
      />}
    </Box>
  );

  // get Dialog content based on the action
  const PayoutSettingsDialogContent = () => {
    switch (dialogContentType) {
      case PayoutDialogContentType.PAYMENT_METHOD_LIST:
        return (<PaymentMethodList />);
      case PayoutDialogContentType.PAYPAL_CONNECT:
        return (<PaypalVerificationCard
          setDialogContentType={setDialogContentType}
          setDialogSubTitle={setDialogSubTitle}
          setPaymentMethodId={setPaymentMethodId}
        />);
      case PayoutDialogContentType.BANK_TRANSFER_FORM:
        return <BankAccountForm
          setOpenDialog={setOpenDialog}
          setDialogContentType={setDialogContentType}
          setDialogSubTitle={setDialogSubTitle}
          setPaymentMethodId={setPaymentMethodId}
        />;
      case PayoutDialogContentType.PAPER_CHECK_DETAILS:
        return <PaperCheckDetails
          setOpenDialog={setOpenDialog}
          setDialogContentType={setDialogContentType}
          setDialogSubTitle={setDialogSubTitle}
          setPaymentMethodId={setPaymentMethodId}
        />;
      case PayoutDialogContentType.SET_ACTIVE_ACCOUNT:
        return <ActivePaymentMethodConfirm
          setDialogContentType={setDialogContentType}
          setDialogSubTitle={setDialogSubTitle}
          onConfirmActiveClick={() => confirmActivePaymentMethod(paymentMethodId)}
        />;
      case PayoutDialogContentType.REMOVE_ACCOUNT:
        return <RemovePaymentMethodConfirm
          setDialogContentType={setDialogContentType}
          setDialogSubTitle={setDialogSubTitle}
          onConfirmDeleteBtnClick={() => confirmDeletePaymentMethod(paymentMethodId)}
        />;
      default:
        return (<PaymentMethodList />);
    }
  };

  return (
    <Dialog
      data-testid={TestIds.walletPage.payoutSettingsDialog}
      open={open}
      onClose={handleClose}
      maxWidth="md"
      // keeping it commented because not sure about the usecase it was explicitly added for
      // fullScreen={isMobile}
      sx={{
        '& .MuiPaper-root': {
          borderRadius: theme.spacing(2.5),
          minWidth: 'sm',
        },
      }}
    >
      <DialogTitle
        data-testid={TestIds.managePayoutSettings.dialogTitle}
        sx={{ p: theme.spacing(3) }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing(1.25),
            textAlign: 'center',
            maxWidth: 'sm',
            justifySelf: 'anchor-center',
          }}
        >
          <Typography
            variant="h12"
            sx={{
              [theme.breakpoints.down('sm')]: {
                fontSize: theme.spacing(2.25),
              },
            }}
          >
            {t('wallet.payoutSettings')}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              [theme.breakpoints.down('sm')]: {
                fontSize: theme.spacing(2),
              },
            }}
          >
            {dialogSubTitle}
          </Typography>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: 'absolute',
              right: theme.spacing(2),
              top: theme.spacing(2),
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent
        sx={{
          overflow: 'auto',
          [theme.breakpoints.down('sm')]: {
            padding: theme.spacing(2),
            paddingBottom: theme.spacing(3),
          },
        }}
      >
        <PayoutSettingsDialogContent />
      </DialogContent>
    </Dialog >
  );
}
